import React, { useState } from 'react';
import { Task, TaskGroup, TaskStatus, UserRole } from '../types/task';
import {
  Badge,
  Button,
  Caption1,
  makeStyles,
  mergeClasses,
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Text,
  Title3,
  tokens,
} from '@fluentui/react-components';
import {
  ChevronRightRegular,
  ChevronDownRegular,
  EditRegular,
  DeleteRegular,
  PersonRegular,
  CalendarRegular,
} from '@fluentui/react-icons';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '64px 32px',
    textAlign: 'center',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
  },
  emptyStateIcon: {
    fontSize: '80px',
    marginBottom: '24px',
    opacity: 0.6,
  },
  groupCard: {
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    overflow: 'hidden',
    boxShadow: tokens.shadow4,
  },
  groupHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '20px 24px',
    cursor: 'pointer',
    backgroundColor: tokens.colorNeutralBackground2,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    transition: 'background-color 0.2s ease',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground3,
    },
  },
  groupTitle: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  groupCount: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  groupIcon: {
    fontSize: '20px',
    color: tokens.colorNeutralForeground2,
  },
  kanbanGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
    gap: '20px',
    padding: '8px',
  },
  kanbanColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    minHeight: '400px',
    padding: '12px',
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusLarge,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  kanbanColumnHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px',
    marginBottom: '8px',
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
    border: `2px solid ${tokens.colorNeutralStroke1}`,
    boxShadow: tokens.shadow2,
  },
  kanbanColumnTitle: {
    fontWeight: tokens.fontWeightBold,
    fontSize: tokens.fontSizeBase400,
    color: tokens.colorNeutralForeground1,
  },
  kanbanColumnCount: {
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground2,
    fontWeight: tokens.fontWeightBold,
    padding: '4px 10px',
    borderRadius: '12px',
    fontSize: tokens.fontSizeBase200,
    minWidth: '28px',
    textAlign: 'center',
  },
  kanbanCard: {
    cursor: 'pointer',
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusLarge,
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow4,
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    overflow: 'hidden',
    position: 'relative',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1,
      boxShadow: tokens.shadow16,
      transform: 'translateY(-4px)',
      border: `1px solid ${tokens.colorBrandStroke1}`,
    },
  },
  kanbanCardContent: {
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  kanbanCardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '8px',
  },
  kanbanCardTitle: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase400,
    color: tokens.colorNeutralForeground1,
    lineHeight: '1.4',
    flex: 1,
  },
  kanbanCardDescription: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground2,
    lineHeight: '1.5',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    marginTop: '4px',
  },
  kanbanCardFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: '12px',
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    gap: '8px',
  },
  kanbanCardMeta: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    flex: 1,
  },
  kanbanCardAssignee: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  kanbanCardDueDate: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: tokens.fontSizeBase200,
  },
  kanbanCardBadges: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
    alignItems: 'center',
  },
  kanbanCardUrgent: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    backgroundColor: tokens.colorPaletteRedBackground2,
  },
  kanbanCardPriorityIndicator: {
    position: 'absolute',
    top: '12px',
    right: '12px',
    width: '8px',
    height: '8px',
    borderRadius: '50%',
  },

  urgentBadge: {
    backgroundColor: tokens.colorPaletteRedBackground2,
    color: tokens.colorPaletteRedForeground2,
    fontWeight: tokens.fontWeightSemibold,
  },
  statusBadge: {
    '&[data-status="open"]': {
      backgroundColor: tokens.colorPaletteBlueBackground2,
      color: tokens.colorPaletteBlueForeground2,
    },
    '&[data-status="in-progress"]': {
      backgroundColor: tokens.colorPaletteYellowBackground2,
      color: tokens.colorPaletteYellowForeground2,
    },
    '&[data-status="complete"]': {
      backgroundColor: tokens.colorPaletteGreenBackground2,
      color: tokens.colorPaletteGreenForeground2,
    },
    '&[data-status="on-hold"]': {
      backgroundColor: tokens.colorNeutralBackground3,
      color: tokens.colorNeutralForeground3,
    },
    '&[data-status="cancelled"]': {
      backgroundColor: tokens.colorPaletteRedBackground2,
      color: tokens.colorPaletteRedForeground2,
    },
  },
  priorityBadge: {
    '&[data-priority="urgent"]': {
      backgroundColor: tokens.colorPaletteRedBackground2,
      color: tokens.colorPaletteRedForeground2,
    },
    '&[data-priority="high"]': {
      backgroundColor: tokens.colorPaletteDarkOrangeBackground2,
      color: tokens.colorPaletteDarkOrangeForeground2,
    },
    '&[data-priority="medium"]': {
      backgroundColor: tokens.colorPaletteYellowBackground2,
      color: tokens.colorPaletteYellowForeground2,
    },
    '&[data-priority="low"]': {
      backgroundColor: tokens.colorPaletteGreenBackground2,
      color: tokens.colorPaletteGreenForeground2,
    },
  },
  overdue: {
    color: tokens.colorPaletteRedForeground2,
    fontWeight: tokens.fontWeightSemibold,
  },

  tableContainer: {
    padding: '0',
  },
  tableHeader: {
    backgroundColor: tokens.colorNeutralBackground2,
    borderBottom: `2px solid ${tokens.colorNeutralStroke1}`,
  },
  tableHeaderCell: {
    padding: '20px',
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
    borderBottom: `3px solid ${tokens.colorNeutralStroke1}`,
  },
  tableCell: {
    padding: '16px 20px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    verticalAlign: 'middle',
    minHeight: '60px',
  },
  tableRow: {
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground2,
    },
  },
  tableRowEven: {
    backgroundColor: tokens.colorNeutralBackground1,
  },
  tableRowOdd: {
    backgroundColor: tokens.colorNeutralBackground3,
  },
  taskTitle: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase400,
    color: tokens.colorNeutralForeground1,
    cursor: 'pointer',
    lineHeight: '1.2',
    ':hover': {
      color: tokens.colorBrandForeground1,
      textDecoration: 'underline',
    },
  },
  assignedToCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontWeight: tokens.fontWeightMedium,
    fontSize: tokens.fontSizeBase300,
  },
  assignedToIcon: {
    color: tokens.colorNeutralForeground2,
    fontSize: '16px',
  },
  dueDateCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: tokens.fontSizeBase300,
  },
  dueDateIcon: {
    color: tokens.colorNeutralForeground2,
    fontSize: '16px',
  },
  actionsCell: {
    display: 'flex',
    gap: '6px',
    justifyContent: 'flex-start',
  },
  actionButton: {
    minWidth: 'auto',
    padding: '4px 8px',
    fontSize: tokens.fontSizeBase200,
  },
  businessUnitCell: {
    fontWeight: tokens.fontWeightMedium,
    color: tokens.colorNeutralForeground1,
    fontSize: tokens.fontSizeBase300,
  },
  badge: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase200,
    padding: '4px 12px',
    borderRadius: '12px',
  },
});

interface TaskTableProps {
  tasks: TaskGroup[];
  onTaskClick: (task: Task) => void;
  onUpdateTask: (taskId: string, updates: Partial<Task>) => void;
  onDeleteTask: (taskId: string) => void;
  userRole: UserRole;
  userId: string;
  viewMode: 'list' | 'kanban';
}

const TaskTable: React.FC<TaskTableProps> = ({
  tasks,
  onTaskClick,
  onUpdateTask,
  onDeleteTask,
  userRole,
  userId,
  viewMode
}) => {
  const styles = useStyles();
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const toggleGroup = (groupKey: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupKey)) {
      newExpanded.delete(groupKey);
    } else {
      newExpanded.add(groupKey);
    }
    setExpandedGroups(newExpanded);
  };

  

  const getStatusColor = (status: TaskStatus) => {
    switch (status) {
      case TaskStatus.OPEN:
        return 'open';
      case TaskStatus.IN_PROGRESS:
        return 'in-progress';
      case TaskStatus.COMPLETE:
        return 'complete';
      case TaskStatus.ON_HOLD:
        return 'on-hold';
      case TaskStatus.CANCELLED:
        return 'cancelled';
      default:
        return 'open';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'Urgent':
        return 'urgent';
      case 'High':
        return 'high';
      case 'Medium':
        return 'medium';
      case 'Low':
        return 'low';
      default:
        return 'medium';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const isOverdue = (task: Task) => {
    if (!task.dueDate) return false;
    return new Date(task.dueDate) < new Date() && task.status !== TaskStatus.COMPLETE;
  };

  const canEditTask = (task: Task) => {
    return userRole === UserRole.EXECUTIVE || task.createdBy === userId || task.assignedTo === userId;
  };

  const canDeleteTask = (task: Task) => {
    return userRole === UserRole.EXECUTIVE || task.createdBy === userId;
  };

  if (tasks.length === 0 || tasks.every(group => group.tasks.length === 0)) {
    return (
      <div className={styles.emptyState}>
        <div className={styles.emptyStateIcon}>📋</div>
        <Title3>No tasks found</Title3>
        <Caption1>Create a new task to get started.</Caption1>
      </div>
    );
  }

  if (viewMode === 'kanban') {
    const getStatusColumnColor = (status: string) => {
      switch (status.toLowerCase()) {
        case 'open':
          return tokens.colorPaletteBlueBackground2;
        case 'in progress':
        case 'in-progress':
          return tokens.colorPaletteYellowBackground2;
        case 'complete':
          return tokens.colorPaletteGreenBackground2;
        case 'on hold':
        case 'on-hold':
          return tokens.colorNeutralBackground3;
        case 'cancelled':
          return tokens.colorPaletteRedBackground2;
        default:
          return tokens.colorNeutralBackground2;
      }
    };

    const getPriorityIndicatorColor = (priority?: string) => {
      switch (priority?.toLowerCase()) {
        case 'urgent':
          return tokens.colorPaletteRedForeground2;
        case 'high':
          return tokens.colorPaletteDarkOrangeForeground2;
        case 'medium':
          return tokens.colorPaletteYellowForeground2;
        case 'low':
          return tokens.colorPaletteGreenForeground2;
        default:
          return tokens.colorNeutralForeground3;
      }
    };

    return (
      <div className={styles.kanbanGrid}>
        {tasks.map(group => (
          <div 
            key={group.key} 
            className={styles.kanbanColumn}
            style={{ 
              borderTop: `4px solid ${getStatusColumnColor(group.label)}`,
            }}
          >
            <div className={styles.kanbanColumnHeader}>
              <Text className={styles.kanbanColumnTitle}>{group.label}</Text>
              <Badge className={styles.kanbanColumnCount}>{group.count}</Badge>
            </div>
            
            {group.tasks.length === 0 ? (
              <div style={{ 
                padding: '24px', 
                textAlign: 'center', 
                color: tokens.colorNeutralForeground3,
                fontSize: tokens.fontSizeBase300,
                fontStyle: 'italic'
              }}>
                No tasks
              </div>
            ) : (
              group.tasks.map(task => (
                <div
                  key={task.id}
                  className={styles.kanbanCard}
                  onClick={() => onTaskClick(task)}
                >
                  {task.urgent && <div className={styles.kanbanCardUrgent} />}
                  
                  <div className={styles.kanbanCardContent}>
                    <div className={styles.kanbanCardHeader}>
                      <Text className={styles.kanbanCardTitle}>{task.title}</Text>
                      <div 
                        className={styles.kanbanCardPriorityIndicator}
                        style={{ backgroundColor: getPriorityIndicatorColor(task.priority) }}
                        title={task.priority || 'Medium'}
                      />
                    </div>
                    
                    {task.description && (
                      <Text className={styles.kanbanCardDescription}>
                        {task.description}
                      </Text>
                    )}
                    
                    <div className={styles.kanbanCardBadges}>
                      {task.urgent && (
                        <Badge 
                          appearance="filled" 
                          className={styles.urgentBadge}
                          style={{ fontSize: tokens.fontSizeBase100 }}
                        >
                          Urgent
                        </Badge>
                      )}
                      <Badge 
                        appearance="filled" 
                        className={mergeClasses(styles.badge, styles.priorityBadge)}
                        data-priority={getPriorityColor(task.priority || 'Medium')}
                        style={{ fontSize: tokens.fontSizeBase100 }}
                      >
                        {task.priority || 'Medium'}
                      </Badge>
                    </div>
                    
                    <div className={styles.kanbanCardFooter}>
                      <div className={styles.kanbanCardMeta}>
                        <div className={styles.kanbanCardAssignee}>
                          <PersonRegular style={{ fontSize: '14px' }} />
                          <Text size={200} style={{ 
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: '150px'
                          }}>
                            {task.assignedTo}
                          </Text>
                        </div>
                        {task.dueDate && (
                          <div className={styles.kanbanCardDueDate}>
                            <CalendarRegular style={{ fontSize: '14px' }} />
                            <Text 
                              size={200}
                              style={{ 
                                color: isOverdue(task) 
                                  ? tokens.colorPaletteRedForeground2 
                                  : tokens.colorNeutralForeground2,
                                fontWeight: isOverdue(task) 
                                  ? tokens.fontWeightSemibold 
                                  : tokens.fontWeightRegular
                              }}
                            >
                              {formatDate(task.dueDate)}
                            </Text>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className={styles.root}>
      {tasks.map(group => (
        <div key={group.key} className={styles.groupCard}>
          <div className={styles.groupHeader} onClick={() => toggleGroup(group.key)}>
            {expandedGroups.has(group.key) ? <ChevronDownRegular className={styles.groupIcon} /> : <ChevronRightRegular className={styles.groupIcon} />}
            <div className={styles.groupTitle}>
              <Text weight="semibold" size={500}>{group.label}</Text>
            </div>
            <div className={styles.groupCount}>
              <Badge appearance="filled" className={styles.badge}>{group.count}</Badge>
            </div>
          </div>
          
          {expandedGroups.has(group.key) && (
            <div className={styles.tableContainer}>
              <Table>
                <TableHeader className={styles.tableHeader}>
                  <TableRow>
                    <TableHeaderCell className={styles.tableHeaderCell}>Task</TableHeaderCell>
                    <TableHeaderCell className={styles.tableHeaderCell}>Assigned To</TableHeaderCell>
                    <TableHeaderCell className={styles.tableHeaderCell}>Status</TableHeaderCell>
                    <TableHeaderCell className={styles.tableHeaderCell}>Priority</TableHeaderCell>
                    <TableHeaderCell className={styles.tableHeaderCell}>Due Date</TableHeaderCell>
                    <TableHeaderCell className={styles.tableHeaderCell}>Department</TableHeaderCell>
                    <TableHeaderCell className={styles.tableHeaderCell}>Actions</TableHeaderCell>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {group.tasks.map((task, index) => (
                    <TableRow 
                      key={task.id} 
                      className={mergeClasses(styles.tableRow, index % 2 === 0 ? styles.tableRowEven : styles.tableRowOdd)}
                    >
                                             <TableCell className={styles.tableCell}>
                         <Text className={styles.taskTitle} onClick={() => onTaskClick(task)}>
                           {task.title}
                         </Text>
                       </TableCell>
                      <TableCell className={styles.tableCell}>
                        <div className={styles.assignedToCell}>
                          <PersonRegular className={styles.assignedToIcon} />
                          <Text>{task.assignedTo}</Text>
                        </div>
                      </TableCell>
                      <TableCell className={styles.tableCell}>
                        <Badge 
                          appearance="filled" 
                          className={mergeClasses(styles.badge, styles.statusBadge)}
                          data-status={getStatusColor(task.status)}
                        >
                          {task.status}
                        </Badge>
                      </TableCell>
                      <TableCell className={styles.tableCell}>
                        <Badge 
                          appearance="filled" 
                          className={mergeClasses(styles.badge, styles.priorityBadge)}
                          data-priority={getPriorityColor(task.priority || 'Medium')}
                        >
                          {task.priority || 'Medium'}
                        </Badge>
                      </TableCell>
                      <TableCell className={styles.tableCell}>
                        {task.dueDate ? (
                          <div className={styles.dueDateCell}>
                            <CalendarRegular className={styles.dueDateIcon} />
                            <Text className={isOverdue(task) ? styles.overdue : ''}>
                              {formatDate(task.dueDate)}
                            </Text>
                          </div>
                        ) : (
                          <Text style={{ color: tokens.colorNeutralForeground3 }}>No due date</Text>
                        )}
                      </TableCell>
                      <TableCell className={styles.tableCell}>
                        <Text className={styles.businessUnitCell}>
                          {task.businessUnit === 'LEHR' ? 'Legal & HR (LEHR)' : task.businessUnit}
                        </Text>
                      </TableCell>
                      <TableCell className={styles.tableCell}>
                        <div className={styles.actionsCell}>
                                                     {canEditTask(task) && (
                             <Button
                               appearance="subtle"
                               icon={<EditRegular />}
                               size="small"
                               className={styles.actionButton}
                               onClick={() => onTaskClick(task)}
                             >
                               View
                             </Button>
                           )}
                          {canDeleteTask(task) && (
                            <Button
                              appearance="subtle"
                              icon={<DeleteRegular />}
                              size="small"
                              className={styles.actionButton}
                              onClick={() => {
                                if (window.confirm('Are you sure you want to delete this task?')) {
                                  onDeleteTask(task.id);
                                }
                              }}
                            >
                              Delete
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default TaskTable; 